import { format, isValid, parseISO } from "date-fns";

import { DealMetricType } from "@vega-api/generated/types/dealMetricType";
import { type DealOverviewMetricResponse } from "@vega-api/generated/types/dealOverviewMetricResponse";
import { coreCurrencies, type CoreCurrency } from "@vega-core/models/money";
import { formatMoney } from "@vega-core/utils/formatMoney";
import { formatNumber, Rounding } from "@vega-core/utils/formatNumber";

import { ANIMATION, SECTION_IDS } from "../constants/overviewConstants";

type CustomMetricsProps = {
  metrics?: DealOverviewMetricResponse[];
  dealCurrency?: CoreCurrency;
};

const isCoreCurrency = (currency: string): currency is CoreCurrency =>
  coreCurrencies.includes(currency as CoreCurrency);

const formatNumericMetric = (
  value: string,
  rounding: Rounding,
  suffix = "",
): string => {
  const parsed = parseFloat(value);

  return isNaN(parsed) ? value : `${formatNumber(parsed, rounding)}${suffix}`;
};

const formatMetricValue = (
  metric: DealOverviewMetricResponse,
  dealCurrency?: CoreCurrency,
): string => {
  if (metric.type === DealMetricType.MONEY && dealCurrency) {
    const amount = parseFloat(metric.value);

    if (!isNaN(amount) && isCoreCurrency(dealCurrency)) {
      return formatMoney({
        amount,
        currency: dealCurrency,
      });
    }
  }

  if (metric.type === DealMetricType.DATE) {
    const date = parseISO(metric.value);

    if (isValid(date)) {
      return format(date, "PPP");
    }

    return metric.value;
  }

  if (metric.type === DealMetricType.PERCENTAGE) {
    return formatNumericMetric(metric.value, Rounding.Always1Fractions, "%");
  }

  if (metric.type === DealMetricType.NUMBER) {
    return formatNumericMetric(metric.value, Rounding.Max2Fractions);
  }

  return metric.value;
};

export const CustomMetrics = ({
  metrics,
  dealCurrency,
}: CustomMetricsProps) => {
  if (!metrics || metrics.length === 0) {
    return null;
  }

  return (
    <div className="flex flex-wrap gap-4" id={SECTION_IDS.KEY_METRICS}>
      {metrics.map((metric, index) => (
        <div
          className="flex-1 min-w-48 p-4 md:p-6 lg:p-8 bg-background/secondary border-radius-companyBorderRadius gap-1 animate-in fade-in slide-in-from-bottom-4 fill-mode-both"
          key={metric.id}
          style={{
            animationDelay: `${index * ANIMATION.DELAY_INCREMENT}ms`,
            animationDuration: `${ANIMATION.DURATION}ms`,
          }}
        >
          <div className="text-text/secondary body-small-regular">
            {metric.name}
          </div>
          <div className="text-text/primary body-extra-large-medium">
            {formatMetricValue(metric, dealCurrency)}
          </div>
        </div>
      ))}
    </div>
  );
};
